import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { UUID } from "crypto";
import { atom, useAtom } from "jotai";
import { useEffect } from "react";
import { ErrorResponse, useNavigate } from "react-router-dom";
import {
  callCompleteStudyAPI,
  callDeleteStudyAPI,
  callDeleteStudyFileAPI,
  callGetEditorStudies,
  ManageStudyListResponse,
  StudyListParams,
} from "../../../api/dataSources";
import emitSnackbar from "../../../emitSnackbar";
import useUserContext from "../../Authorization/useUserContext";
import { ManageStudiesState } from "../models";

const ManageStudiesStateAtom = atom<ManageStudiesState>({
  studyCounts: {},
  isAPILoading: false,
  // studyId: undefined, // TODO: This does NOT belong in the store
  openModal: false,
  isDeleteLoading: false,
  audioFile: [],
});
const StudyListParamsAtom = atom<StudyListParams>({ permission_level: "OWNER", page: 1, page_size: 10 });

export const useManageStudiesStore = () => {
  const user = useUserContext();
  const navigate = useNavigate();
  const [ManageStudies, setManageStudies] = useAtom(ManageStudiesStateAtom);
  const queryClient = useQueryClient();
  // Any state variables used in a hook that is called in multiple places HAS to use an atom
  const [StudyListParams, setStudyListParams] = useAtom(StudyListParamsAtom);
  const setStudyId = (studyId: string) => {
    setManageStudies((_) => ({ ..._, studyId }));
  };

  const setListView = (view: "OWNER" | "EDITOR") => {
    setStudyListParams((_) => ({ ..._, page: 1, permission_level: view }));
  };

  const setPage = (page: number) => {
    setStudyListParams((_) => ({ ..._, page }));
  };

  const getViewRecordCount = (view: "OWNER" | "EDITOR") => {
    return ManageStudies.studyCounts[view];
  };

  const setOpenModal = (value: boolean) => {
    setManageStudies((_) => ({ ..._, openModal: value }));
  };

  // Get editable studies
  const { data, isLoading, isRefetching, refetch } = useQuery<ManageStudyListResponse>({
    queryKey: ["pmr:managed_studies", StudyListParams],
    queryFn: () => callGetEditorStudies(user, StudyListParams),
  });

  useEffect(() => {
    if (data?.total_count) {
      setManageStudies((_) => {
        const next = { ..._, studyCounts: { ..._.studyCounts } };
        // Sync the study count to the target "permission_level" so we can keep a count per view
        next.studyCounts[StudyListParams.permission_level] = data.total_count;
        return next;
      });
    }
  }, [data, StudyListParams]);

  const completeStudy = async (studyId: UUID) => {
    try {
      setManageStudies((prev) => ({ ...prev, isAPILoading: true }));
      const response = await callCompleteStudyAPI(user, `${studyId}`);
      if (response) {
        navigate(-1);
        emitSnackbar("Study completed", "success", { autoHideDuration: 3000 });
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        const axiosError = error as AxiosError<ErrorResponse>;
        emitSnackbar(axiosError.response?.data["detail"] ?? "Something went wrong", "error", {
          autoHideDuration: 3000,
        });
      } else {
        emitSnackbar("Something went wrong, please try again later", "error", {
          autoHideDuration: 3000,
        });
      }
    } finally {
      setManageStudies((prev) => ({ ...prev, isAPILoading: false }));
    }
  };

  const deleteStudy = async (studyId: UUID) => {
    try {
      setManageStudies((prev) => ({ ...prev, isDeleteLoading: true }));
      const response = await callDeleteStudyAPI(user, `${studyId}`);
      if (response) {
        queryClient.invalidateQueries({
          queryKey: ["pmr:managed_studies"],
        });
        emitSnackbar("Study Deleted", "success", { autoHideDuration: 3000 });
        refetch();
      }
    } catch (error) {
      console.error("Error calling  API:", error);
      emitSnackbar("Error deleting study, please try again after sometime", "error", { autoHideDuration: 3000 });
    } finally {
      setManageStudies((prev) => ({ ...prev, isDeleteLoading: false, openModal: false }));
    }
  };

  const deleteStudyFile = async (fileGuid: UUID) => {
    try {
      setManageStudies((prev) => ({ ...prev, isDeleteLoading: true }));
      const response = await callDeleteStudyFileAPI(user, `${fileGuid}`);
      if (response) {
        emitSnackbar("File Deleted", "success", { autoHideDuration: 3000 });
        updateAudioFile(fileGuid);
        refetch();
      }
    } catch (error) {
      console.error("Error calling  API:", error);
      emitSnackbar("Error deleting study, please try again after sometime", "error", { autoHideDuration: 3000 });
    } finally {
      setManageStudies((prev) => ({ ...prev, isDeleteLoading: false, openModal: false }));
    }
  };

  const resetState = () => {
    setManageStudies((_) => ({ ..._, studyId: undefined, isAPILoading: false }));
  };

  const updateAudioFile = (fileGuid: string) => {
    setManageStudies((prev) => ({
      ...prev,
      audioFile: prev.audioFile.map((file) => {
        if (file.file_guid === fileGuid) {
          return { ...file, uploaded_file: null };
        }
        return file;
      }),
    }));
  };

  return {
    navigate,
    data,
    isLoading,
    isRefetching,
    StudyListParams,
    setPage,
    setListView,
    ManageStudies,
    getViewRecordCount,
    completeStudy,
    setStudyId,
    resetState,
    refetch,
    deleteStudy,
    setOpenModal,

    deleteStudyFile,
    updateAudioFile,
  };
};
